import { createSelector } from "reselect";
// eslint-disable-next-line @logicsoftware/ep-custom/no-cross-feature-imports
import { getCurrentUserId } from "~/app/selectors/common";
import { ApproverStatus } from "~/const/approval";
import { getItemsSetForLimitedTooltip, ItemsSetForLimitedTooltip } from "~/EasyProjectsCommon/General";
import { lingvoUtility } from "~/EasyProjectsCommon/LingvoUtility";
import { Entities } from "~/normalizr/entities";
import { messageBoardResx } from "~/resxDist/messageBoardResx";
import { epFormat } from "~/utils/epFormat";
import { SortUtility } from "~/utils/sort";
import { ApprovalsModel, Approver, ProjectOrTask, RootMessageBoardState } from "../types";
import { getApprovals } from "./messages";

const getApproval = (state: RootMessageBoardState, ownerInfo: ProjectOrTask, id: number) =>
    getApprovals(state, ownerInfo)?.[id];

export const getCurrentUserApprover = (approvers: Entities<Approver>, currentUserId: number) =>
    approvers?.[currentUserId];
const isSomebodyApprovedOrRejected = (approvers: Approver[]) =>
    approvers.some(x => x.Status === ApproverStatus.Rejected || x.Status === ApproverStatus.Approved);
const isCompleted = (approvers: Approver[]) =>
    approvers.every(x => x.Status === ApproverStatus.Approved || x.Status === ApproverStatus.Rejected);
const isApproved = (approvers: Approver[]) => approvers.every(x => x.Status === ApproverStatus.Approved);
const isRejected = (approvers: Approver[]) => approvers.every(x => x.Status === ApproverStatus.Rejected);
const getStatusMessage = (approvers: Approver[], isActive: boolean) => {
    const getPositivePart = () => {
        const approvedCount = approvers.filter(x => x.Status === ApproverStatus.Approved).length;

        if (!approvedCount && !isActive && !isSomebodyApprovedOrRejected(approvers)) {
            return "";
        }

        const pluralApprove = lingvoUtility.plural(approvedCount, messageBoardResx.PluralApproved);

        return epFormat(messageBoardResx.MultipleApproversTemplate, approvedCount, approvers.length, pluralApprove);
    };

    const getNegativePart = () => {
        const rejectedCount = approvers.filter(x => x.Status === ApproverStatus.Rejected).length;

        if (!rejectedCount) {
            return "";
        }

        return lingvoUtility.plural(rejectedCount, messageBoardResx.PluralRejected);
    };

    if (approvers.length === 1) {
        return;
    }

    const approved = getPositivePart();
    const rejected = getNegativePart();

    return approved + (approved && rejected ? ", " : "") + rejected;
};

const getSingleApproverMessage = (approvers: Approver[], currentUserApprover: Approver, isActive: boolean) => {
    if (currentUserApprover?.Status === ApproverStatus.Approved) {
        // approved by you
        return messageBoardResx.SingleApprovedTemplate_You;
    } else if (currentUserApprover?.Status === ApproverStatus.Rejected) {
        // rejected by you
        return messageBoardResx.SingleRejectedTemplate_You;
    } else if (approvers.length === 1) {
        if (isApproved(approvers)) {
            // approved by smbd
            return epFormat(messageBoardResx.SingleApprovedTemplate, approvers[0].UserName);
        } else if (isCompleted(approvers)) {
            // rejected by smbd
            return epFormat(messageBoardResx.SingleRejectedTemplate, approvers[0].UserName);
        } else if (!currentUserApprover && isActive) {
            // waiting for smbd, active approval
            return epFormat(messageBoardResx.SingleApproverTemplate, approvers[0].UserName);
        }
    }
};

const getSingleActionDate = (approvers: Approver[]) => {
    if (approvers.length > 1 || (!isApproved(approvers) && !isRejected(approvers))) {
        return;
    }

    return approvers[0].ActionDate;
};

export type ApprovalMessage = {
    status: ApproverStatus;
    statusMessage: string;
    single: string;
    date: string;
    isActionBtnVisible: boolean;
    isCompleted: boolean;
};

export const getApprovalData = (approval: ApprovalsModel, currentUserId: number): ApprovalMessage => {
    if (!approval) {
        return null;
    }

    const { approvers, IsActive } = approval;
    const approversArray = Object.values(approvers);

    const currentUserApprover = getCurrentUserApprover(approvers, currentUserId);
    const statusMessage = getStatusMessage(approversArray, IsActive);
    const single = getSingleApproverMessage(approversArray, currentUserApprover, IsActive);
    const date = getSingleActionDate(approversArray);
    const isMeApprover = !!currentUserApprover;
    const status = currentUserApprover?.Status;
    const isMeApproved = status === ApproverStatus.Approved;
    const isMeRejected = status === ApproverStatus.Rejected;
    const isApprovalCompleted = isMeApproved || isMeRejected;

    const isActionBtnVisible = IsActive && isMeApprover && !isApprovalCompleted;
    const isCompleted = IsActive && isApprovalCompleted;

    return { status, statusMessage, single, date, isActionBtnVisible, isCompleted };
};

export const getApprovalMessage = createSelector(getApproval, getCurrentUserId, getApprovalData);

const sortApproversByUserName = (a: Approver, b: Approver) => SortUtility.sortWithLocalCompare(a.UserName, b.UserName);
const getLimitApprovers = (approval: ApprovalsModel): ItemsSetForLimitedTooltip<Approver> => {
    if (!approval) {
        return null;
    }

    const sortedApprovers = Object.values(approval.approvers).sort(sortApproversByUserName);
    return getItemsSetForLimitedTooltip(sortedApprovers);
};

export const getApprovers = createSelector(getApproval, getLimitApprovers);
